<template>
  <div class="row">
    <!-- Name column -->
    <span v-tooltip="tooltipOptions" :title="null">{{ name }}</span>
    <!-- Before column (value before rendering) -->
    <span :class="{ editorchanged: hasChangedByUser }">
      <!-- Input element for any non-boolean type -->
      <input
        v-if="type != VARIABLES.BOOLEAN.type"
        :value="before"
        :title="null"
        :type="inputMode.type"
        :step="inputMode.step"
        class="before-value"
        placeholder="Empty string"
        spellcheck="false"
        @blur="onBlurValue"
        @keyup.enter="$event.target.blur()"
      />
      <!-- Select (true|false) element for boolean type -->
      <select
        v-else
        :title="null"
        class="before-value"
        @change="onChangeBooleanValue"
      >
        <option value="true" :selected="before">true</option>
        <option value="false" :selected="!before">false</option>
      </select>
    </span>
    <!-- After column (value after rendering) -->
    <span :class="{ changed: hasChangedByPlay }">
      <input
        :value="after"
        :title="null"
        type="text"
        class="after-value"
        placeholder="Empty string"
        spellcheck="false"
        disabled
      />
    </span>
  </div>
</template>

<script>
import { VARIABLES } from '../../constants.js';

/**
 * Our constant tooltip options
 */
const TOOLTIP_OPTIONS = {
  placement: 'left',
  distance: 15,
  boundary: document.body,
  color: 'primary',
  popperClass: ['secondary'],
};

export default {
  props: {
    /**
     * The variable type
     */
    type: {
      type: String,
      default: null,
    },

    /**
     * The variable id
     */
    id: {
      type: String,
      default: null,
    },

    /**
     * The variable name
     */
    name: {
      type: String,
      default: null,
    },

    /**
     * The variable value before the rendering of the protototype screen
     */
    initialBefore: {
      type: [String, Number, Boolean],
      default: null,
    },

    /**
     * The variable value before the rendering of the protototype screen
     */
    before: {
      type: [String, Number, Boolean],
      default: null,
    },

    /**
     * The variable value after the rendering of the prototype screen
     */
    after: {
      type: [String, Number, Boolean],
      default: null,
    },
  },

  /**
   * Component data properties
   */
  data() {
    return {
      VARIABLES,
    };
  },

  computed: {
    /**
     * The tooltip options for the v-tooltip component on the name (displays the type)
     * @returns {Object}
     */
    tooltipOptions() {
      return {
        // The dynamic content of the tooltip
        content: this.type,

        // The constant tooltip options
        ...TOOLTIP_OPTIONS,
      };
    },

    /**
     * Whether the variable value has changed (before and after are different)
     * @returns {boolean}
     */
    hasChangedByPlay() {
      return this.before !== this.after;
    },

    /**
     * Whether the variable before value has been changed manually by the user
     */
    hasChangedByUser() {
      return this.initialBefore !== this.before;
    },

    /**
     * Returns the appropriate type for the HTML input control (number or text)
     * @returns {string}
     */
    inputMode() {
      switch (this.type) {
        // On integer variable type create a numeric input
        case VARIABLES.INT.type: {
          return { type: 'number', step: '1' };
        }
        // On number (float) variable type create a decimal input
        case VARIABLES.NUMBER.type: {
          return { type: 'number', step: '0.1' };
        }
        // On string variable type create a text input
        case VARIABLES.STRING.type: {
          return { type: 'text' };
        }
        // Other cases are not allowed
        default:
          throw new Error('Invalid type for input:', this.type);
      }
    },
  },

  methods: {
    /**
     * Event handler for blur event on the before value field
     * @param {FocusEvent} event
     */
    onBlurValue(event) {
      let { value } = event.target;
      value = this.castValueToSpecificType(value, this.type);
      // Do nothing if the value is unchanged
      if (value === this.before) return;

      this.$emit('updateBeforeValue', { id: this.id, value });
    },

    /**
     * Event handler for selection change in select for boolean value.
     * This select element is shown only when the type is 'boolean'.
     */
    onChangeBooleanValue(event) {
      let { value } = event.target;
      value = this.castValueToSpecificType(value, this.type);
      // Do nothing if the value is unchanged
      if (value === this.before) return;

      this.$emit('updateBeforeValue', { id: this.id, value });
    },

    /**
     * Returns a value according to the variable type
     * @param {string} value The string value to be cast
     * @param {string} type An arcscript variable type
     * @returns {mixed}
     */
    castValueToSpecificType(value, type) {
      switch (type) {
        // Attempt to convert the string value to an integer
        case VARIABLES.INT.type: {
          const result = parseInt(value, 10);
          if (Number.isNaN(result)) return 0;
          return result;
        }
        // Attempt to convert the string value to a float
        case VARIABLES.NUMBER.type: {
          const result = parseFloat(value);
          if (Number.isNaN(result)) return 0.0;
          return result;
        }
        // Attempt to convert the string value to a boolean
        case VARIABLES.BOOLEAN.type: {
          return value === 'true';
        }
        // Convert the string value to a string (unnecessary)
        case VARIABLES.STRING.type: {
          return String(value);
        }
        // Other cases are not allowed
        default:
          throw new Error('Invalid casting to type:', type);
      }
    },
  },
};
</script>
<style scoped lang="scss">
.row {
  display: inline-grid;
  position: relative;
  grid-template-columns: repeat(3, 0.7fr);
  padding: 0 12px 5px;
  font-family: monospace;
  font-size: 13px;
  > span {
    padding: 24px 10px;
    border-bottom: 1px solid #2c3131;
    overflow: hidden;
    text-overflow: ellipsis;
    &:nth-child(-n + 3) {
      border-bottom: none;
      padding: 14px 8px 8px;
    }
    &:nth-child(1n + 0) {
      width: 100%;
    }
    &:nth-last-child(-n + 3) {
      border-bottom: none;
    }
    strong {
      text-transform: uppercase;
      font-size: 13px;
      font-weight: 600;
      opacity: 0.5;
    }
    &:nth-child(3n + 0) {
      text-align: right;
    }
  }
}

input {
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  width: 100%;
}

input,
select {
  border: none;
  color: #bcbcbc;
  background-color: transparent;
  padding: 6px 10px;
  border-radius: 3px;
  word-break: break-word;
  font-size: 13px;
  margin: -8px 0px 0 0 !important;
  min-width: 0;
  font-family: monospace;
  transition: background-color 0.1s;
  &.name {
    // color: #67cdfe;
    flex: 3;
  }
  &.type {
    flex: 1;
  }
  &.before-value {
    flex: 1;
  }
  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }
  &:last-child {
    margin: 0;
  }
  &:focus {
    background-color: #202323;
  }
  &:disabled {
    background: none;
    // border: 1px solid #3c4f52;
    // color: #bcbcbc !important;
    cursor: default;
  }
}
select {
  padding: 6px 34px 6px 10px;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-image: url("data:image/svg+xml;utf8,<svg fill='%23bcbcbc' height='24' viewBox='0 0 24 24' width='24' xmlns='http://www.w3.org/2000/svg'><path d='M7 10l5 5 5-5z'/><path d='M0 0h24v24H0z' fill='none'/></svg>");
  background-position: right 5px top 3px;
  background-repeat: no-repeat;
  option {
    font-family: monospace;
  }
}
input.before-value:hover,
select.before-value:hover {
  background-color: #252929;
}
.type {
  flex: 1;
}

.row {
  // &::before {
  //   content: '';
  //   position: absolute;
  //   top: 50%;
  //   margin-top: -3px;
  //   width: 6px;
  //   height: 6px;
  //   background: #db841e;
  //   border-radius: 999px;
  //   left: 4px;
  // }
  span.changed * {
    color: #db841e !important;
  }
  span.editorchanged * {
    color: #00d0ff !important;
  }
}
</style>
